import {
  leadSourceMap,
  type LeadSource,
} from '@mortgage-pos/data/leadSourcesMap'
import { LeadSourceType } from '@mortgage-pos/types'

/**
 *
 * @returns "name" in StudlyCaps/UpperCamelCase
 */
export function getBankrateLeadSource(
  offerTags: string[],
  isPrimaryLead?: boolean,
  landingUrl?: string,
  leadSource?: string
): LeadSource {
  if (leadSource?.toUpperCase().includes(LeadSourceType.Amazon)) {
    return leadSourceMap.AMAZON
  }
  // BankrateEmail leads do not have a bankrateLeadId, so they get picked up via the generic url match logic
  // BankrateHomeEquity leads only have a bankRateLeadId, so also check via url here
  if (isPrimaryLead === true) {
    if (landingUrl?.includes(leadSourceMap.HOMEEQUITYPRIMARY.urlMatchString)) {
      return leadSourceMap.HOMEEQUITYPRIMARY
    }

    return leadSourceMap.BANKRATEPRIMARY
  }
  if (offerTags && offerTags.includes('digiFlex')) {
    return leadSourceMap.BANKRATEDIGIFLEX
  }

  if (landingUrl) {
    const landingUrlSource = getSourceByLandingUrl(String(landingUrl))
    if (
      landingUrlSource?.name &&
      ['Bankrate', 'HomeEquity'].some((s) => landingUrlSource?.name.includes(s))
    ) {
      return landingUrlSource
    }
  }

  // (isPrimaryLead === false) || NULL
  return leadSourceMap.BANKRATESECONDARY
}

export function getLeadSource(leadSourceName: string): LeadSource {
  // Match to map by key, trimmed & case-insensitive
  if (leadSourceName.trim().toUpperCase() in leadSourceMap) {
    return leadSourceMap[leadSourceName.trim().toUpperCase()]
  }
  return {
    name: leadSourceName,
  }
}

export function isBuilder(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.isBuilder === true
}

export function isPubCashout(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.isPubCashout === true
}

export function isEmployeeLoans(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.EmployeeLoans
}

export function isRobinhood(leadSourceName: string): boolean {
  if (!leadSourceName) return false
  const source = getLeadSource(leadSourceName)
  return source?.name?.toUpperCase() === LeadSourceType.Robinhood
}

export function getSource(sourceData: SourceData) {
  // Match to map by the source data containing a field with a name matching "sourceField"
  for (const key in leadSourceMap) {
    if (sourceData[leadSourceMap[key].sourceField]) {
      return leadSourceMap[key].name
    }
  }

  return leadSourceMap[LeadSourceType.SageWebsite].name
}

export function getSourceByLandingUrl(landingUrl: string) {
  const strictMatches = Object.keys(leadSourceMap).reduce((acc, key) => {
    if (typeof leadSourceMap[key].urlMatchParams !== 'undefined') {
      acc.push(key)
    }
    return acc
  }, [])

  // Sort by the number of parameters in the urlMatchParams array
  // in order of most parameters to least to match on stricter sources first
  // (i.e. RVMN has multiple related sources and is treated as the fall back)
  strictMatches.sort((a, b) => {
    const aLength = leadSourceMap[a].urlMatchParams?.length || 0
    const bLength = leadSourceMap[b].urlMatchParams?.length || 0
    return bLength - aLength
  })

  // Check for multiple parameter matches first
  for (const key of strictMatches) {
    if (
      leadSourceMap[key].urlMatchParams.every((param) =>
        landingUrl.includes(param)
      )
    ) {
      return leadSourceMap[key]
    }
  }

  for (const key in leadSourceMap) {
    if (
      typeof leadSourceMap[key].urlMatchString !== 'undefined' &&
      landingUrl.includes(leadSourceMap[key].urlMatchString)
    ) {
      return leadSourceMap[key]
    }

    if (
      typeof leadSourceMap[key].urlSecondaryMatchString !== 'undefined' &&
      landingUrl.includes(leadSourceMap[key].urlSecondaryMatchString)
    ) {
      return leadSourceMap[key]
    }
  }
}

type SourceData = {
  fBLeadId?
  bankrateLeadId?
  gclid?
}

export const leadSourcesWithConversionTracking = [
  LeadSourceType.RateZip,
  LeadSourceType.TheMortgageReport,
  LeadSourceType.MilitaryVALoan,
] as const

export const leadSourceNamesWithConversionTracking =
  leadSourcesWithConversionTracking.map((source) => getLeadSource(source).name)
